import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserRole = 'STAFF' | 'ADMIN' | 'FINANCE' | 'FINANCE_LEAD' | 'HR' | 'IT_ADMIN';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  mustChangePassword?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionError: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearSessionError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      sessionError: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, sessionError: null });

        try {
          const response = await fetch('/api/v1/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // Include session cookie
            body: JSON.stringify({ email, password }),
          });

          const result = await response.json();

          if (!response.ok) {
            set({ isLoading: false, sessionError: result.error?.message ?? 'Login failed' });
            throw new Error(result.error?.message ?? 'Login failed');
          }

          set({
            user: result.data.user,
            isAuthenticated: true,
            isLoading: false,
            sessionError: null,
          });
          const { syncManager } = await import('@/lib/syncManager');
          void syncManager.processQueue();
        } catch (err) {
          set({ isLoading: false });
          if (!(err instanceof Error && err.message.includes('failed'))) {
            set({ sessionError: 'Network error. Please try again.' });
          }
          throw err;
        }
      },

      logout: async () => {
        try {
          await fetch('/api/v1/auth/logout', {
            method: 'POST',
            credentials: 'include',
          });
        } catch {
          // Always clear local state even if server request fails
        }
        set({ user: null, isAuthenticated: false, sessionError: null });
      },

      clearSessionError: () => set({ sessionError: null }),
    }),
    {
      name: 'rhpc-auth',
      // Only persist user state (not loading/error)
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

/**
 * Get role-based dashboard route
 */
export function getDashboardRoute(role: UserRole): string {
  switch (role) {
    case 'STAFF': return '/dashboard';
    case 'ADMIN': return '/admin/dashboard';
    case 'FINANCE':
    case 'FINANCE_LEAD': return '/finance/dashboard';
    case 'IT_ADMIN': return '/admin/system-config';
    default: return '/dashboard';
  }
}
